/**
 * Hooks de gestión de usuarios y roles
 * Casos de uso: USR-01, USR-02
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type { Role, TenantMembership } from '@/types'

// ─── Keys de cache ────────────────────────────────────────────────────────────
export const userKeys = {
  list: ['users'] as const,
  roles: ['users', 'roles'] as const,
  sellers: ['users', 'sellers'] as const,
}

// ─── Tipos locales ────────────────────────────────────────────────────────────
interface TenantUser {
  id: string
  email: string
  full_name: string
  phone: string
  avatar_url: string | null
  roles: { id: number; type: string; name: string }[]
  membership_status: 'pending' | 'accepted' | 'revoked'
  is_active: boolean
  last_seen_at: string | null
  invited_at: string
  accepted_at: string | null
}

interface InviteUserPayload {
  email: string
  first_name?: string
  last_name?: string
  role_ids: number[]
  sso_only?: boolean
}

interface CreateRolePayload {
  name: string
  role_type: string
  description?: string
  permissions: Record<string, string[]>
  eligible_for_commissions?: boolean
  pos_capabilities?: string[]
  pos_max_discount_pct?: string
}

// ─── USR-01: Listar usuarios del tenant ───────────────────────────────────────
export function useTenantUsers() {
  return useQuery({
    queryKey: userKeys.list,
    queryFn: async () => {
      const { data } = await apiClient.get<{ users: TenantUser[]; count: number }>('/users/')
      return data
    },
    staleTime: 2 * 60 * 1000,
  })
}

// ─── USR-01: Invitar usuario ──────────────────────────────────────────────────
export function useInviteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: InviteUserPayload) => {
      const { data } = await apiClient.post('/users/invite/', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list })
    },
  })
}

// ─── USR-01: Modificar roles ──────────────────────────────────────────────────
export function useUpdateUserRoles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: number[] }) => {
      const { data } = await apiClient.patch(`/users/${userId}/roles/`, { role_ids: roleIds })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list })
    },
  })
}

// ─── USR-01: Revocar acceso ───────────────────────────────────────────────────
export function useRevokeUserAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/users/${userId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list })
    },
  })
}

// ─── USR-01: Resetear contraseña (admin) ─────────────────────────────────────
export function useAdminResetPassword() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await apiClient.post(`/users/${userId}/reset-password/`)
      return data as { detail: string; email: string }
    },
  })
}

// ─── USR-01: Reenviar invitación ──────────────────────────────────────────────
export function useResendInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await apiClient.post(`/users/${userId}/resend-invitation/`)
      return data as { detail: string; email: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list })
    },
  })
}

// ─── Vendedores (SalesRep) para atribución ────────────────────────────────────
// `id` es el id del SalesRep (vendedor canónico), que es lo que se asigna a
// pedidos/documentos/contratos vía `sales_rep`.
export interface SellerItem {
  id: string
  name: string
  email: string
  full_name: string
  has_system_access: boolean
  user_id: string | null
}

export function useSellers() {
  return useQuery({
    queryKey: userKeys.sellers,
    queryFn: async () => {
      const { data } = await apiClient.get<{ sellers: SellerItem[]; count: number }>('/users/sellers/')
      return data
    },
    staleTime: 2 * 60 * 1000,
  })
}

// ─── USR-02: Listar roles ─────────────────────────────────────────────────────
export function useRoles() {
  return useQuery({
    queryKey: userKeys.roles,
    queryFn: async () => {
      const { data } = await apiClient.get<{ roles: Role[] }>('/users/roles/')
      return data.roles
    },
    staleTime: 10 * 60 * 1000, // Los roles cambian poco
  })
}

// ─── USR-02: Crear rol ────────────────────────────────────────────────────────
export function useCreateRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateRolePayload) => {
      const { data } = await apiClient.post<Role>('/users/roles/create/', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles })
    },
  })
}

// ─── USR-02: Actualizar permisos de rol ───────────────────────────────────────
export function useUpdateRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ roleId, ...payload }: Partial<CreateRolePayload> & { roleId: number }) => {
      const { data } = await apiClient.patch<Role>(`/users/roles/${roleId}/`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles })
    },
  })
}

// ─── USR-02: Eliminar rol ─────────────────────────────────────────────────────
export function useDeleteRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (roleId: number) => {
      await apiClient.delete(`/users/roles/${roleId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles })
    },
  })
}
